import React, { useCallback, useEffect, useState, useContext } from 'react'
import { createGenesysConversation } from 'api/support'
import AssistChip from 'components/Luxkit/Chips/AssistChip'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import { rem } from 'polished'
import styled from 'styled-components'
import { isLoggedIn } from 'selectors/accountSelectors'
import { useAppSelector } from 'hooks/reduxHooks'
import { SocketContext } from '../../contexts/SocketContext'

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-items: end;
  justify-content: end;
  grid-gap: ${rem(8)};
  margin-bottom: ${rem(12)};
`

interface AgentProps {
  socket: any;
  activeChat?: any;
  onMessageSend: (message: string, isSystemMessage?: boolean) => void;
  onSelectChat: (chatId: string) => void;
  outageMessage?: string;
  onResetOutageMessage: () => void;
  customer: {
    firstName?: string;
    lastName?: string;
  };
  loadingMessages?: boolean;
}

export function Agent({
  socket,
  activeChat,
  customer,
  onSelectChat,
  outageMessage,
  onResetOutageMessage,
  loadingMessages,
}: AgentProps) {
  const [supportAgentSuggestion, setSupportAgentSuggestion] = useState<boolean>(false)
  const [agentRequestedChatId, setAgentRequestedChatId] = useState<string | null>(null)

  const userIsLoggedIn = useAppSelector(isLoggedIn)

  const { onLogin } = useContext(SocketContext)

  useEffect(() => {
    if (outageMessage) {
      setSupportAgentSuggestion(true)
      setAgentRequestedChatId(activeChat?.id)
    }
  }, [outageMessage, activeChat?.id])

  useEffect(() => {
    if (socket) {
      socket.on('chat:prompt:support', ({ hasDeadEnd, hasAgentRequest, chatId }) => {
        if (hasDeadEnd || hasAgentRequest) {
          setSupportAgentSuggestion(true)
          if (chatId) {
            setAgentRequestedChatId(chatId)
            onSelectChat(chatId)
          }
        }
      })

      return () => {
        socket.off('chat:prompt:support')
      }
    }
  }, [onSelectChat, socket])

  const onConnectToSupport = useCallback(async(customer) => {
    setSupportAgentSuggestion(false)
    setAgentRequestedChatId(null)
    try {
      await createGenesysConversation({
        chatId: activeChat?.id,
        customer,
      })
      onResetOutageMessage()
    } catch (error) {
      console.warn(error)
    }
  }, [activeChat?.id, onResetOutageMessage])

  const onRejectSupport = useCallback(() => {
    setSupportAgentSuggestion(false)
    setAgentRequestedChatId(null)
  }, [])

  const onLoginClick = useCallback(() => {
    onLogin().then(({ loggedIn, firstName, lastName }) => {
      if (activeChat?.id && loggedIn) {
        onConnectToSupport({ firstName, lastName })
      }
    })
  }, [onLogin, onConnectToSupport, activeChat?.id])

  const showSupportAgentSuggestion = !loadingMessages && supportAgentSuggestion && agentRequestedChatId == activeChat?.id

  return (
    <>
      {showSupportAgentSuggestion && userIsLoggedIn && <>
        <BodyTextBlock variant="small">
          Connect to a support agent?
        </BodyTextBlock>
        <ActionsContainer>
          <AssistChip
            variant="filled"
            size="medium"
            onClick={() => onConnectToSupport(customer)}
          >
            Yes, please connect me to an agent
          </AssistChip>
          {!outageMessage && <AssistChip
            variant="filled"
            size="medium"
            onClick={onRejectSupport}
          >
            No thank you, I want to keep speaking to you!
          </AssistChip>}
        </ActionsContainer>
      </>}
      {showSupportAgentSuggestion && !userIsLoggedIn && <>
        <BodyTextBlock variant="small">
          Log in to connect to Support Agent
        </BodyTextBlock>
        <ActionsContainer>
          <AssistChip
            variant="filled"
            size="medium"
            onClick={onLoginClick}
          >
            Log in and connect to agent
          </AssistChip>
          <AssistChip
              variant="filled"
              size="medium"
              onClick={onRejectSupport}
            >
            No thank you
          </AssistChip>
        </ActionsContainer>
      </>}
    </>
  )
}
